import "./SimulationTradeComponent.scss";
import {IComponentOptions, IOnChanges, IOnChangesObject, IOnDestroy, IOnInit, IRootElementService} from "angular";
import {ReplaySubject, Subject} from "rxjs";
import {ComponentCallback} from "../../../../types/AngularJS";
import {
    HistoricalOptionRecord,
    HistoricalPriceRange
} from "../../../../services/price/historical/HistoricalPriceModels";
import {SimulationTradeDetail} from "../models/Trade";
import {SimulationCalculationOptionValue, SimulationResultContract} from "../../../models/TradeSimulationRecords";
import {TradeSimulatorViewStore} from "../../../service/TradeSimulatorViewStore";
import {SimulationTrade} from "../../../models/SimulationTrade";
import {OptionChain} from "../../../../services/option/chain/OptionChain";
import {OptionChainService} from "../../../../services/option-chain/OptionChainService";
import {ObjectUtil} from "../../../../js/util/ObjectUtil";
import {PriceStrategy} from "../../../../components/inputs/price/input/PriceStrategy";
import {PriceStrategyService} from "../../../../components/inputs/price/service/PriceStrategyService";
import {IsoDate} from "../../../../pseudo-types/DateTypes";

export const SimulationTradeRow = "tfSimulationTrade";

export function simulationTradeRow(): IComponentOptions {
    return {
        templateUrl: "app/trade-simulator/components/simulation-data/trade/SimulationTradeComponent.html",
        bindings: {
            viewStore: "<",
            detail: "<",
            underlying: "<",
            expiries: "<",
            onChange: "&",
            onDelete: "&",
            onClose: "&"
        },
        controllerAs: "ctrl",
        controller: SimulationTradeComponentController
    };
}

const enum ChangeDirection {
    NEXT, PREVIOUS
}

export class SimulationTradeComponentController implements IOnInit, IOnChanges, IOnDestroy {

    public viewStore: TradeSimulatorViewStore;
    public underlying: string;
    public detail: SimulationTradeDetail;
    public expiries: Array<Date>;
    public minDate: string;
    public maxDate: string;
    public onChange: ComponentCallback;
    public onClose: ComponentCallback;

    public trade: SimulationTrade;
    public optionValue: SimulationCalculationOptionValue | undefined;
    public contract: SimulationResultContract | undefined;
    public holidays: Array<Date>;
    public priceStrategy: PriceStrategy;

    private chain$ = new ReplaySubject<OptionChain>(1);

    private expiryChanges$ = new Subject<ChangeDirection>();
    private strikeChanges$ = new Subject<ChangeDirection>();

    private changes$ = new Subject();
    private readonly destroyed$ = new ReplaySubject<boolean>(1);

    /** @ngInject */
    public constructor(private $element: IRootElementService,
                       private priceStrategyService: PriceStrategyService,
                       private optionChainService: OptionChainService) {
        this.changes$
            .takeUntil(this.destroyed$)
            .debounceTime(500)
            .subscribe(this.checkForChanges.bind(this));
    }

    public $onInit(): void {
        this.viewStore
            .priceRange
            .takeUntil(this.destroyed$)
            .subscribe(this.updateDateRange.bind(this));

        this.viewStore
            .holidays
            .takeUntil(this.destroyed$)
            .subscribe(holidays => this.holidays = holidays);

        this.expiryChanges$
            .takeUntil(this.destroyed$)
            .withLatestFrom(this.chain$)
            .subscribe(([change, chain]) => this.selectNewExpiry(change, chain));

        this.strikeChanges$
            .takeUntil(this.destroyed$)
            .withLatestFrom(this.chain$)
            .subscribe(([change, chain]) => this.selectNewStrike(change, chain));
    }

    private selectNewExpiry(change: ChangeDirection, chain: OptionChain) {
        if (change === ChangeDirection.NEXT) {
            chain.nextExpiry(this.trade.code)
                 .ifPresent(this.updateTrade.bind(this));
        } else {
            chain.previousExpiry(this.trade.code)
                 .ifPresent(this.updateTrade.bind(this));
        }
    }

    private selectNewStrike(change: ChangeDirection, chain: OptionChain) {
        if (change === ChangeDirection.NEXT) {
            chain.nextStrike(this.trade.code)
                 .ifPresent(this.updateTrade.bind(this));
        } else {
            chain.previousStrike(this.trade.code)
                 .ifPresent(this.updateTrade.bind(this));
        }
    }

    private updateTrade(record: HistoricalOptionRecord) {
        this.trade.code = record.code;
        this.trade.price = record.openPrice;
        this.contract = ObjectUtil.merge({}, this.contract, record);
        this.queueChangeDetection();
    }

    public $onChanges(changes: IOnChangesObject): void {
        if (changes.detail != null && this.detail != null) {
            this.trade = this.detail.trade;
            this.optionValue = this.detail.value;
            this.contract = this.detail.contract;
            if (this.detail.hasError) {
                this.$element.addClass("contract-error");
            } else {
                this.$element.removeClass("contract-error");
            }
            this.retrieveChain(this.underlying, this.trade.date);
        }
        if (changes.underlying != null && this.underlying != null) {
            this.priceStrategy = this.priceStrategyService.forCode(this.underlying);
        }
    }

    private async retrieveChain(underlying: string, asAt: string) {
        const chain = await this.optionChainService.chainFor(underlying, asAt);
        this.chain$.next(chain);
    }

    private updateDateRange(priceRange: HistoricalPriceRange) {
        if (priceRange != null) {
            this.minDate = priceRange.from;
            this.maxDate = priceRange.to;

            if (this.trade != null && this.trade.contract != null) {
                const contractExpiry = this.trade.contract.expiry;
                if (contractExpiry < this.maxDate) {
                    this.maxDate = contractExpiry;
                }
            }
        }
    }

    private checkForChanges() {
        if (this.trade.hasChanges) {
            this.onChange({trade: this.trade});
        }
    }

    private queueChangeDetection() {
        this.changes$.next();
    }

    public dateChanged(date: IsoDate) {
        this.trade.date = date;
        this.queueChangeDetection();
    }

    public codeChanged(option: HistoricalOptionRecord) {
        this.trade.code = option.code;
        this.queueChangeDetection();
    }

    public quantityChanged() {
        this.queueChangeDetection();
    }

    public nextStrike() {
        this.strikeChanges$.next(ChangeDirection.NEXT);
    }

    public previousStrike() {
        this.strikeChanges$.next(ChangeDirection.PREVIOUS);
    }

    public nextExpiry() {
        this.expiryChanges$.next(ChangeDirection.NEXT);
    }

    public previousExpiry() {
        this.expiryChanges$.next(ChangeDirection.PREVIOUS);
    }

    public priceChanged(price: number) {
        this.trade.price = price;
        this.queueChangeDetection();
    }

    public closeTrade() {
        this.onClose({tradeDetail: this.detail});
    }

    public $onDestroy(): void {
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }
}
